import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Phone, MessageCircle, Video, Clock, CheckCircle,
  ShieldCheck, Star, Send, X, ArrowLeft, CreditCard,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";
import { PayPalCheckoutButton } from "../components/PayPalCheckoutButton";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  {
    id: "basic",
    planId: "counseling_basic",
    title: "기본 상담",
    desc: "30분 전화 또는 줌 상담으로 고민을 나눠보세요.",
    duration: "30분",
    price: 50,
    features: ["1:1 전화/줌 상담", "맞춤형 조언", "상담 후 요약 메시지 제공"],
    popular: false,
  },
  {
    id: "deep",
    planId: "counseling_deep",
    title: "심층 상담",
    desc: "60분 집중 상담으로 깊은 대화를 나눠보세요.",
    duration: "60분",
    price: 90,
    features: ["1:1 줌 상담", "심층 분석 & 맞춤 솔루션", "1주일 후 팔로업 상담", "상담 보고서 제공"],
    popular: true,
  },
  {
    id: "package",
    planId: "counseling_package",
    title: "4주 코칭 패키지",
    desc: "4주 동안 매주 1회씩 체계적인 코칭을 받으세요.",
    duration: "4주 (주 1회, 60분)",
    price: 300,
    features: ["주 1회 줌 상담 (총 4회)", "개인 맞춤 코칭 플랜", "카카오톡 수시 상담", "매 회 상담 보고서", "목표 달성 체크리스트"],
    popular: false,
  },
];

export default function LizCounselingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [applyMethod, setApplyMethod] = useState("zoom");
  const [applyMessage, setApplyMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleApply = async () => {
    if (!applyMessage.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: `[리즈상담 신청] ${selectedService?.title}`,
          content: `상담 방식: ${applyMethod === "zoom" ? "줌" : applyMethod === "phone" ? "전화" : "카카오톡"}\n\n${applyMessage}`,
          category: "general",
        }),
      });
      if (res.ok) {
        setShowApplyModal(false);
        setApplyMessage("");
        setShowSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "신청에 실패했습니다.");
      }
    } catch {
      alert("신청에 실패했습니다.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-stone-800 via-stone-900 to-stone-800" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-rose-500/15 border border-rose-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-rose-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">리즈 1:1 상담</h1>
          <p className="text-stone-400 text-base sm:text-lg max-w-lg mx-auto leading-relaxed">
            더 깊고 자세한 이야기가 필요하신가요?<br />
            리즈 코치와 <span className="text-rose-400 font-semibold">1:1 프라이빗 상담</span>을 받아보세요.
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-stone-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> 100% 비밀보장</span>
            <span className="w-px h-4 bg-stone-600" />
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4" /> 전문 코치</span>
            <span className="w-px h-4 bg-stone-600" />
            <span className="flex items-center gap-1.5"><Video className="w-4 h-4" /> 줌/전화/카톡</span>
          </div>
        </div>
      </section>

      {/* Service Cards */}
      <section className="max-w-4xl mx-auto px-6 -mt-8 relative z-10">
        <div className="grid md:grid-cols-3 gap-5">
          {SERVICES.map((service) => (
            <div
              key={service.id}
              className={`bg-white rounded-2xl border overflow-hidden shadow-sm hover:shadow-lg transition-shadow relative ${
                service.popular ? "border-rose-200 ring-2 ring-rose-100" : "border-stone-200"
              }`}
              data-testid={`service-card-${service.id}`}
            >
              {service.popular && (
                <div className="bg-rose-500 text-white text-xs font-bold text-center py-1.5">
                  BEST
                </div>
              )}
              <div className="p-6">
                <h3 className="text-lg font-bold text-stone-800 mb-1">{service.title}</h3>
                <p className="text-sm text-stone-500 mb-4">{service.desc}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold text-stone-800">USD ${service.price}</span>
                </div>
                <p className="text-xs text-stone-400 mb-5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{service.duration}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full py-3 font-bold rounded-xl ${
                    service.popular
                      ? "bg-rose-500 hover:bg-rose-600 text-white"
                      : "bg-stone-800 hover:bg-stone-900 text-white"
                  }`}
                  onClick={() => {
                    if (!isAuthenticated) { navigate("/"); return; }
                    setSelectedService(service);
                    setShowApplyModal(true);
                    setApplyMethod("zoom");
                    setApplyMessage("");
                  }}
                  data-testid={`apply-btn-${service.id}`}
                >
                  상담 신청하기
                </Button>
                <div className="mt-2">
                  <PayPalCheckoutButton
                    planId={service.planId}
                    amount={service.price}
                    label={service.title}
                    testIdPrefix={`pay-${service.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info Section */}
      <section className="max-w-3xl mx-auto px-6 mt-16 mb-10">
        <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 mb-6 text-center">리즈 상담은 이런 분께 추천드려요</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "비공개 상담소 답변으로 부족했던 분",
              "복잡한 연애/재혼 상황을 깊이 이야기하고 싶은 분",
              "전문가의 체계적인 코칭이 필요한 분",
              "혼자 고민하기 어려운 중요한 결정을 앞둔 분",
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-stone-700">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />

      {/* Apply Modal */}
      <AnimatePresence>
        {showApplyModal && selectedService && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowApplyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-stone-800 p-5 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">{selectedService.title} 신청</h3>
                  <button onClick={() => setShowApplyModal(false)} className="text-stone-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-stone-400 text-sm mt-1">
                  USD ${selectedService.price} &middot; {selectedService.duration}
                </p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">상담 방식</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { val: "zoom", label: "줌", icon: Video },
                      { val: "phone", label: "전화", icon: Phone },
                      { val: "kakao", label: "카카오톡", icon: MessageCircle },
                    ].map(({ val, label, icon: Icon }) => (
                      <button
                        key={val}
                        onClick={() => setApplyMethod(val)}
                        className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                          applyMethod === val
                            ? "bg-stone-800 text-white border-stone-800"
                            : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                        }`}
                        data-testid={`method-${val}`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">상담 내용</label>
                  <textarea
                    value={applyMessage}
                    onChange={(e) => setApplyMessage(e.target.value)}
                    placeholder="어떤 고민으로 상담을 받고 싶으신지 간략히 작성해주세요. 상담 전 미리 파악하여 준비합니다."
                    className="w-full h-32 px-4 py-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-stone-300"
                    maxLength={1000}
                    data-testid="apply-message-textarea"
                  />
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs text-stone-500">신청 후 리즈 코치가 메시지함으로 상담 일정을 안내해 드립니다.</p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Button
                  className="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl"
                  onClick={handleApply}
                  disabled={submitting || !applyMessage.trim()}
                  data-testid="submit-apply-btn"
                >
                  {submitting ? "신청 중..." : "상담 신청하기"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="success-popup"
            >
              <div className="bg-gradient-to-b from-rose-500 to-rose-600 pt-8 pb-6 px-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle className="w-9 h-9 text-rose-500" />
                </div>
                <h3 className="text-xl font-bold text-white">상담 신청 완료!</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-stone-600">
                  리즈 코치가 메시지함으로<br />상담 일정을 안내해 드립니다.
                </p>
                <Button
                  className="w-full py-3 bg-stone-800 hover:bg-stone-900 text-white font-bold rounded-xl"
                  onClick={() => setShowSuccess(false)}
                  data-testid="success-close-btn"
                >
                  확인
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
